import { getValueOrDefault, isEmpty } from "@utils/var.check";
import {
  SEARCH_MODE,
  RESULT_CARD_TYPE,
  QUESTION_RESOURCE_TYPE_OPTIONS,
  MEDIA_RESOURCE_TYPE_OPTIONS,
  LESSON_TYPE_OPTIONS,
} from "@modules/search/service/search.constant";
import store from "@modules/search/components/SearchStore";
import { RESOURCE_TYPE } from "@plugins/common/constant/resource.constant";

export default {
  inject: ["searchContext"],
  data() {
    return {
      isSelectedCard: false,
      store: store,
    };
  },
  computed: {
    resource() {
      return this.searchContext.resourceType;
    },
    isSelection() {
      return this.isSingleSelection || this.isMultipleSelection;
    },
    isSingleSelection() {
      return (
        !isEmpty(this.searchContext) && this.searchContext.mode === SEARCH_MODE.SINGLE_SELECTION
      );
    },
    isMultipleSelection() {
      return (
        !isEmpty(this.searchContext) &&
        (this.searchContext.mode === SEARCH_MODE.MULTI_SELECTION ||
          this.searchContext.mode === SEARCH_MODE.MULTI_SELECTION_COMPONENT)
      );
    },
    selectedClass() {
      return {
        selected: this.isSelectedCard,
      };
    },
    subpageTitle() {
      return this.searchContext.title ? [...this.searchContext.title] : "";
    },
    isDisabledResource() {
      return (
        !isEmpty(this.searchContext && this.searchContext.preFilter) &&
        !isEmpty(this.searchContext && this.searchContext.preFilter.query.resource)
      );
    },
    isDisabledResourceType() {
      // Disable resource type if pre-filtered by a specific media resource type
      return (
        this.isLessonResourceSelected ||
        this.isRubricResourceSelected ||
        this.isAppResourceSelected ||
        this.isWebsiteResourceSelected
      );
    },
    isLessonResourceSelected() {
      return this.store.getResourceSelected() === RESOURCE_TYPE.LESSON;
    },
    isRubricResourceSelected() {
      return this.store.getResourceSelected() === RESOURCE_TYPE.RUBRIC;
    },
    isAppResourceSelected() {
      return this.store.getResourceSelected() === RESOURCE_TYPE.APP;
    },
    isWebsiteResourceSelected() {
      return this.store.getResourceSelected() === RESOURCE_TYPE.WEBSITE;
    },
    getPreFilteredResourceTypeLabel() {
      if (
        !isEmpty(this.searchContext.preFilter) &&
        this.searchContext.preFilter.query.resource === "MEDIA"
      ) {
        if (this.searchContext.preFilter.query.resourceType.length !== 1) {
          return this.$i18n.t("search_filter_resource_type_select_label");
        }

        const filteredResourceType = this.searchContext.preFilter.query.resourceType[0];
        const resourceTypePrefilter = MEDIA_RESOURCE_TYPE_OPTIONS.find((option) => {
          return option.value === filteredResourceType;
        });

        if (!isNull(resourceTypePrefilter)) {
          return this.$i18n.t(resourceTypePrefilter.label);
        }

        return this.$i18n.t("search_filter_resource_type_select_label");
      } else if (
        !isEmpty(this.searchContext.preFilter) &&
        this.searchContext.preFilter.query.resource === "QUESTION"
      ) {
        const filteredResourceType = this.searchContext.preFilter.query.resourceType[0];
        const resourceTypePrefilter = QUESTION_RESOURCE_TYPE_OPTIONS.find((option) => {
          return option.value === filteredResourceType;
        });
        if (!isNull(resourceTypePrefilter)) {
          return this.$i18n.t(resourceTypePrefilter.label);
        }
        return this.$i18n.t("search_filter_resource_type_select_label");
      } else {
        return this.$i18n.t("search_filter_resource_type_select_label");
      }
    },
    isSelectionPagination() {
      return (searchFilters) => {
        if (
          this.searchContext.resourceType === "MEDIA" ||
          this.searchContext.resourceType === "QUESTION"
        ) {
          return searchFilters.resourceType.length === 1;
        } else {
          // Apply pagination for other resource types (LESSON, QUESTION, QUIZ)
          return true;
        }
      };
    },
  },
  methods: {
    getResourceType(searchFilters) {
      return searchFilters.resource;
    },
    getPreFilteredLocation(searchFilters) {
      // ALL, MINE, MOE, COMMUNITY
      if (!isEmpty(this.searchContext.preFilter)) {
        const location = this.searchContext.preFilter.query.location;
        return location instanceof Array ? "ALL" : location;
      } else {
        return searchFilters.location;
      }
    },
    getPreFilteredResourceTypes(resource) {
      let resourceTypes = [];
      if (!isEmpty(this.searchContext && this.searchContext.preFilter)) {
        const filteredResourceType = getValueOrDefault(
          this.searchContext.preFilter.query.resourceType,
          [],
        );
        switch (this.searchContext.preFilter.query.resource) {
          case "MEDIA": {
            MEDIA_RESOURCE_TYPE_OPTIONS.forEach((type) => {
              resourceTypes.push({
                label: this.$i18n.t(type.label),
                value: type.value,
                name: this.$i18n.t(type.name),
                disabled:
                  !isEmpty(filteredResourceType) && !filteredResourceType.includes(type.value),
              });
            });
            break;
          }
          case "QUESTION": {
            if (isEmpty(filteredResourceType)) {
              QUESTION_RESOURCE_TYPE_OPTIONS.forEach((type) => {
                resourceTypes.push({
                  label: this.$i18n.t(type.label),
                  value: type.value,
                  name: this.$i18n.t(type.name),
                });
              });
            } else {
              QUESTION_RESOURCE_TYPE_OPTIONS.forEach((type) => {
                filteredResourceType.forEach((resourceType) => {
                  if (type.value === resourceType) {
                    resourceTypes.push({
                      label: this.$i18n.t(type.label),
                      value: type.value,
                      name: this.$i18n.t(type.name),
                    });
                  }
                });
              });
            }
            break;
          }
          case "LESSON": {
            if (isEmpty(filteredResourceType)) {
              LESSON_TYPE_OPTIONS.forEach((type) => {
                resourceTypes.push({
                  label: this.$i18n.t(type.label),
                  value: type.value,
                  name: type.name,
                });
              });
            }
            break;
          }
          default:
            break;
        }
      } else {
        // Return all resource types as default
        switch (resource) {
          case "MEDIA": {
            MEDIA_RESOURCE_TYPE_OPTIONS.forEach((type) => {
              resourceTypes.push({
                label: this.$i18n.t(type.label),
                value: type.value,
                name: this.$i18n.t(type.name),
              });
            });
            break;
          }
          case "QUESTION": {
            QUESTION_RESOURCE_TYPE_OPTIONS.forEach((type) => {
              resourceTypes.push({
                label: this.$i18n.t(type.label),
                value: type.value,
                name: this.$i18n.t(type.name),
              });
            });
            break;
          }
          case "LESSON": {
            LESSON_TYPE_OPTIONS.forEach((type) => {
              resourceTypes.push({
                label: this.$i18n.t(type.label),
                value: type.value,
                name: type.name,
              });
            });
            break;
          }
          default:
            break;
        }
      }
      return resourceTypes;
    },
    getMediaFromArray(index) {
      let media;
      MEDIA_RESOURCE_TYPE_OPTIONS.forEach((mediaType) => {
        if (mediaType.value === index) {
          media = mediaType.name;
        }
      });
      return media;
    },
    getQuestionFromArray(index) {
      let question;
      QUESTION_RESOURCE_TYPE_OPTIONS.forEach((questionType) => {
        if (questionType.value === index) {
          question = questionType.name;
        }
      });
      return question;
    },
    addSelection() {
      this.isSelectedCard = true;
      this.store.setSelectionList({
        uuid: this.resourceData.uuid,
        type:
          this.searchContext.resourceType === RESULT_CARD_TYPE.MEDIA
            ? this.fileType
            : this.resourceData.questionType,
        title: this.resourceData.title,
        resourceDomain: this.resourceData.resourceDomain,
        description: this.resourceData.description,
      });
    },
    removeSelection(uuid) {
      this.isSelectedCard = false;
      this.store.removeSelectionList(uuid);
    },
    handleClearSelection() {
      const selection = this.store.getSelectionByUuid(this.resourceData.uuid);
      this.isSelectedCard = !isEmpty(selection);
    },
  },
};
